// Hand-maintained — override generation
import {
  getAdminAccounts,
  getAdminAccountsById,
  patchAdminAccountsByIdCredit,
  patchAdminAccountsByIdDebit,
} from "../_internal/sdk.gen";
import type { Account } from "../_internal/types.gen";
import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";

/**
 * Manages platform billing accounts.
 *
 * Each account tracks the credit balance and currency for an API key or workspace.
 * Admin access only — these operations bypass tenant-level authorization.
 */
export function createAccountsNamespace(rb: RequestBuilder) {
  return {
    /**
     * Lists all billing accounts across the platform.
     *
     * @param options - Optional request options (headers, abort signal).
     * @returns Array of Account objects with current balances.
     */
    list: async (options?: RequestOptions): Promise<Account[]> => {
      return rb.execute<Account[]>(getAdminAccounts, {}, options);
    },

    /**
     * Fetches a single billing account by ID.
     *
     * @param id - Account ID.
     * @param options - Optional request options.
     * @returns The Account record.
     */
    get: async (id: string, options?: RequestOptions): Promise<Account> => {
      return rb.execute<Account>(
        getAdminAccountsById,
        { path: { id } },
        options,
      );
    },

    /**
     * Adds credits to an account balance.
     *
     * Use for manual top-ups, promotional credits, or correction adjustments.
     * The amount must be a positive number greater than zero.
     *
     * @param id - Account ID.
     * @param amount - Number of credits to add (must be > 0).
     * @param description - Optional human-readable reason for the credit.
     * @param options - Optional request options.
     * @returns Updated Account with the new balance.
     * @example
     * ```typescript
     * const account = await admin.accounts.credit(
     *   "acct_123",
     *   5000,
     *   "Manual top-up",
     * );
     * ```
     */
    credit: async (
      id: string,
      amount: number,
      description?: string,
      options?: RequestOptions,
    ): Promise<Account> => {
      if (amount <= 0) {
        throw new Error("Credit amount must be positive");
      }
      return rb.execute<Account>(
        patchAdminAccountsByIdCredit,
        {
          path: { id },
          body: {
            data: { type: "account", attributes: { amount, description } },
          },
        },
        options,
      );
    },

    /**
     * Deducts credits from an account balance.
     *
     * Use for charge-backs, manual usage adjustments, or corrections.
     * The amount must be a positive number greater than zero.
     *
     * @param id - Account ID.
     * @param amount - Number of credits to deduct (must be > 0).
     * @param description - Optional human-readable reason for the debit.
     * @param options - Optional request options.
     * @returns Updated Account with the new balance.
     * @example
     * ```typescript
     * const account = await admin.accounts.debit(
     *   "acct_123",
     *   250,
     *   "Usage correction",
     * );
     * ```
     */
    debit: async (
      id: string,
      amount: number,
      description?: string,
      options?: RequestOptions,
    ): Promise<Account> => {
      if (amount <= 0) {
        throw new Error("Debit amount must be positive");
      }
      return rb.execute<Account>(
        patchAdminAccountsByIdDebit,
        {
          path: { id },
          body: {
            data: { type: "account", attributes: { amount, description } },
          },
        },
        options,
      );
    },
  };
}
